import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    Truck,
    Boxes,
    Globe2,
    Snowflake,
    Container,
    Warehouse,
} from "lucide-react";

const SERVICES = [
    {
        icon: Truck,
        title: "Full Truckload (FTL)",
        desc: "Dedicated trucks for high-volume, time-sensitive freight. Direct routes, no co-loading.",
    },
    {
        icon: Boxes,
        title: "Less-than-Truckload (LTL)",
        desc: "Cost-effective consolidation for smaller shipments without sacrificing reliability.",
    },
    {
        icon: Container,
        title: "Containerised Freight",
        desc: "Port-to-door coordination including drayage and final-mile delivery.",
    },
    {
        icon: Snowflake,
        title: "Refrigerated Transport",
        desc: "Temperature-controlled carriers for perishables, pharma, and sensitive cargo.",
    },
    {
        icon: Globe2,
        title: "Cross-Border Coordination",
        desc: "Customs documentation, border clearances, and regional carrier coverage handled end-to-end.",
    },
    {
        icon: Warehouse,
        title: "Project & Heavy Haul",
        desc: "Specialised equipment, route surveys, and permits for oversize or project loads.",
    },
];

const Services = () => {
    return (
        <div data-testid="services-page" className="relative">
            <section className="relative pt-40 pb-20 md:pt-48 md:pb-28">
                <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
                <div className="relative mx-auto max-w-5xl px-5 md:px-8">
                    <p className="mono text-xs text-brand-green uppercase mb-5">
                        / Services
                    </p>
                    <h1 className="display text-5xl md:text-7xl text-[#1a1a1a]">
                        Every load.<br />
                        <span className="text-brand-blue glow-text">
                            Every route.
                        </span>
                    </h1>
                    <p className="mt-8 text-gray-700 text-lg max-w-2xl leading-relaxed">
                        From single pallets to oversize freight, Nexship
                        coordinates the right carrier for every job — with the
                        right equipment, on the right schedule.
                    </p>
                </div>
            </section>

            <section className="relative py-16 md:py-20">
                <div className="mx-auto max-w-7xl px-5 md:px-8">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {SERVICES.map((s, i) => (
                            <div
                                key={s.title}
                                data-testid={`service-${i}`}
                                className="group brand-border-soft rounded-2xl p-8 hover:translate-y-[-4px] transition-all duration-300"
                            >
                                <div className="h-12 w-12 rounded-xl bg-brand-green/10 flex items-center justify-center mb-6 group-hover:bg-brand-green/20 transition-colors">
                                    <s.icon
                                        size={22}
                                        className="text-brand-green"
                                    />
                                </div>
                                <h3 className="text-xl font-semibold text-[#1a1a1a] mb-3">
                                    {s.title}
                                </h3>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    {s.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="relative py-28">
                <div className="mx-auto max-w-5xl px-5 md:px-8">
                    <div className="brand-border rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
                        <div className="absolute -top-32 -right-32 h-72 w-72 rounded-full bg-brand-green/15 blur-3xl" />
                        <div className="absolute -bottom-32 -left-32 h-72 w-72 rounded-full bg-brand-blue/15 blur-3xl" />
                        <h2 className="display text-3xl md:text-5xl text-[#1a1a1a] relative">
                            Don't see your load type?
                        </h2>
                        <p className="mt-5 text-gray-600 max-w-xl mx-auto relative">
                            If it can be moved, we can coordinate it. Tell us
                            what you need.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4 justify-center relative">
                            <Link
                                to="/request-transport"
                                className="btn-primary"
                                data-testid="services-cta-request"
                            >
                                Request Transport <ArrowUpRight size={16} />
                            </Link>
                            <Link to="/contact" className="btn-ghost">
                                Talk To Us
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Services;
